import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, ArrowLeft, Users2, Search, Filter } from "lucide-react";
import ContactForm from "@/components/contacts/ContactForm";
import ContactsList from "@/components/contacts/ContactsList";
import VendorsList from "@/components/vendors/VendorsList";
import VendorForm from "@/components/vendors/VendorForm";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from 'react-i18next';

const CREW_ROLES = [
  "All Roles",
  "Director",
  "Producer",
  "Director of Photography",
  "Camera Operator",
  "First AC",
  "Second AC",
  "Gaffer",
  "Best Boy Electric",
  "Key Grip",
  "Best Boy Grip",
  "Production Designer",
  "Audio Mixer",
  "Other",
] as const;

export default function Contacts() {
  const [showContactForm, setShowContactForm] = useState(false);
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState<string>("All Roles");
  const [activeTab, setActiveTab] = useState<string>("crew");
  const { user } = useAuth();
  const { t } = useTranslation();

  const { data: contacts, refetch: refetchContacts } = useQuery({
    queryKey: ["crew-contacts"],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("crew_contacts")
        .select("*")
        .eq('user_id', user.id)
        .order("created_at", { ascending: false });

      if (error) {
        toast.error(t('failed_load_crew_contacts'));
        throw error;
      }

      return data || [];
    },
    enabled: !!user,
  });

  const { data: vendors, refetch: refetchVendors } = useQuery({
    queryKey: ["vendors"],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("vendors")
        .select("*")
        .eq('user_id', user.id)
        .order("name", { ascending: true });

      if (error) {
        toast.error(t('failed_load_vendors'));
        throw error;
      }

      return data || [];
    },
    enabled: !!user,
  });

  const filteredContacts = contacts?.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.notes?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = selectedRole === "All Roles" || contact.role === selectedRole;
    
    return matchesSearch && matchesRole;
  });

  const handleCreateContact = async (contact: any) => {
    try {
      const { error } = await supabase
        .from("crew_contacts")
        .insert([{ ...contact, user_id: user?.id }]);

      if (error) throw error;

      toast.success(t('contact_created'));
      refetchContacts();
      setShowContactForm(false);
    } catch (error) {
      console.error("Error creating contact:", error);
      toast.error(t('failed_create_contact'));
    }
  };

  const handleUpdateContact = async (id: string, contact: any) => {
    try {
      const { error } = await supabase
        .from("crew_contacts")
        .update(contact)
        .eq("id", id);

      if (error) throw error;

      toast.success(t('contact_updated'));
      refetchContacts();
    } catch (error) {
      console.error("Error updating contact:", error);
      toast.error(t('failed_update_contact'));
    }
  };

  const handleDeleteContact = async (id: string) => {
    try {
      const { error } = await supabase
        .from("crew_contacts")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success(t('contact_deleted'));
      refetchContacts();
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error(t('failed_delete_contact'));
    }
  };

  const handleCreateVendor = async (vendor: any) => {
    try {
      const { error } = await supabase
        .from("vendors")
        .insert([{ ...vendor, user_id: user?.id }]);

      if (error) throw error;

      toast.success(t('vendor_created'));
      refetchVendors();
      setShowVendorForm(false);
      setEditingVendor(null);
    } catch (error) {
      console.error("Error creating vendor:", error);
      toast.error(t('failed_create_vendor'));
    }
  };

  const handleUpdateVendor = async (vendor: any) => {
    try {
      const { error } = await supabase
        .from("vendors")
        .update(vendor)
        .eq("id", editingVendor.id);

      if (error) throw error;

      toast.success(t('vendor_updated'));
      refetchVendors();
      setShowVendorForm(false);
      setEditingVendor(null);
    } catch (error) {
      console.error("Error updating vendor:", error);
      toast.error(t('failed_update_vendor'));
    }
  };

  const handleVendorFormSubmit = (data: any) => {
    if (editingVendor) {
      handleUpdateVendor(data);
    } else {
      handleCreateVendor(data);
    }
  };

  const handleEditVendor = (vendor: any) => {
    setEditingVendor(vendor);
    setShowVendorForm(true);
  };

  return (
    <div className="site-gradient min-h-screen">
      <div className="container mx-auto py-8 px-4">
        <div className="flex flex-col space-y-8">
          {/* Header Section */}
          <div className="flex flex-col space-y-4">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="h-8 px-2">
                  <ArrowLeft className="h-4 w-4" />
                  <span className="ml-2">{t('back')}</span>
                </Button>
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <Users2 className="h-6 w-6 text-primary" />
                </div>
                <h1 className="text-2xl font-brakle font-semibold text-white">{t('contacts')}</h1>
              </div>
              {activeTab === "crew" && (
                <Button 
                  onClick={() => {
                    setShowContactForm(true);
                  }}
                  className="w-full sm:w-auto transition-all hover:shadow-lg bg-primary hover:bg-primary/90 font-brakle"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('add_contact')}
                </Button>
              )}
              {activeTab === "vendors" && (
                <Button 
                  onClick={() => {
                    setEditingVendor(null);
                    setShowVendorForm(true);
                  }}
                  className="w-full sm:w-auto transition-all hover:shadow-lg bg-primary hover:bg-primary/90 font-brakle"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('add_vendor')}
                </Button>
              )}
            </div>
          </div>

          {/* Tabs Section */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="crew">{t('crew')}</TabsTrigger>
              <TabsTrigger value="vendors">{t('vendors')}</TabsTrigger>
            </TabsList>

            <TabsContent value="crew">
              {/* Search and Filter Section */}
              <div className="glass-card p-4 mb-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t('search_contacts')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-black/20 border-0"
                    />
                  </div>
                  <Select
                    value={selectedRole}
                    onValueChange={setSelectedRole}
                  >
                    <SelectTrigger className="w-full sm:w-[200px] bg-black/20 border-0">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder={t('filter_by_role')} />
                    </SelectTrigger>
                    <SelectContent>
                      {CREW_ROLES.map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Contact List Section */}
              <div className="glass-card p-6">
                {contacts && contacts.length > 0 ? (
                  <ContactsList
                    contacts={filteredContacts || []}
                    onEdit={handleUpdateContact}
                    onDelete={handleDeleteContact}
                  />
                ) : (
                  <div className="text-center py-12">
                    <Users2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t('no_contacts_yet')}</h3>
                    <p className="text-muted-foreground mb-4">
                      {t('start_building_crew')}
                    </p>
                    <Button 
                      onClick={() => setShowContactForm(true)}
                      variant="outline"
                      className="font-brakle"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t('add_first_contact')}
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="vendors">
              <div className="glass-card p-6">
                {vendors && vendors.length > 0 ? (
                  <VendorsList 
                    vendors={vendors} 
                    onEdit={handleEditVendor}
                  />
                ) : (
                  <div className="text-center py-12">
                    <Users2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t('no_vendors_yet')}</h3>
                    <p className="text-muted-foreground mb-4">
                      {t('add_vendors_description')}
                    </p>
                    <Button 
                      onClick={() => {
                        setEditingVendor(null);
                        setShowVendorForm(true);
                      }}
                      variant="outline"
                      className="font-brakle"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t('add_first_vendor')}
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {showContactForm && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <ContactForm
              onClose={() => setShowContactForm(false)}
              onSubmit={handleCreateContact}
            />
          </div>
        )}

        {showVendorForm && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <VendorForm
              onClose={() => {
                setShowVendorForm(false);
                setEditingVendor(null);
              }}
              onSubmit={handleVendorFormSubmit}
              initialData={editingVendor}
            />
          </div>
        )}
      </div>
    </div>
  );
}
